import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AcademicCapIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckIcon,
  Bars3Icon,
  XMarkIcon,
  PlayIcon,
  StarIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CloudIcon,
  BoltIcon,
  EyeIcon,
  CpuChipIcon,
  WifiIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  RocketLaunchIcon,
  HeartIcon,
  UserGroupIcon,
  CreditCardIcon,
  TruckIcon,
  MapPinIcon,
  SignalIcon,
  QrCodeIcon,
  GlobeAltIcon,
  DocumentChartBarIcon,
  BellIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const smartTagFeatures = [
    {
      icon: QrCodeIcon,
      title: 'RFID Student Tracking',
      description: 'Real-time location monitoring using advanced RFID wristband technology with instant parent notifications.',
      gradient: 'from-blue-500 to-cyan-500',
      delay: 0,
      module: 'SmartTag Track™'
    },
    {
      icon: TruckIcon,
      title: 'Transport Management',
      description: 'Complete fleet management with GPS tracking, route optimization, and real-time bus location updates.',
      gradient: 'from-green-500 to-emerald-500',
      delay: 0.1,
      module: 'SmartTag Transport'
    },
    {
      icon: CreditCardIcon,
      title: 'RFID Terminals & POS',
      description: 'Cashless payments with RFID cards for meals, books, and school services. Secure and convenient.',
      gradient: 'from-purple-500 to-pink-500',
      delay: 0.2,
      module: 'SmartTag Pay™'
    },
    {
      icon: DocumentChartBarIcon,
      title: 'Advanced Analytics',
      description: 'AI-powered insights and comprehensive reporting for administrators, teachers, and parents.',
      gradient: 'from-orange-500 to-red-500',
      delay: 0.3,
      module: 'SmartTag Analytics'
    },
    {
      icon: UserGroupIcon,
      title: 'Multi-Tenant Platform',
      description: 'Scalable architecture supporting multiple schools with role-based access and tenant isolation.',
      gradient: 'from-indigo-500 to-blue-500',
      delay: 0.4,
      module: 'SmartTag Platform'
    },
    {
      icon: BellIcon,
      title: 'Instant Communication',
      description: 'Real-time notifications, emergency alerts, and seamless communication between all stakeholders.',
      gradient: 'from-yellow-500 to-orange-500',
      delay: 0.5,
      module: 'SmartTag Connect'
    },
  ]

  const platformModules = [
    {
      title: 'Academic Management',
      features: ['Grades & Streams', 'Lesson Planning', 'Session Tracking', 'Attendance Automation'],
      icon: AcademicCapIcon,
      color: 'from-blue-500 to-purple-500'
    },
    {
      title: 'RFID & Hardware',
      features: ['RFID Terminals', 'Smart Cards', 'POS Systems', 'Network Health'],
      icon: CpuChipIcon,
      color: 'from-green-500 to-teal-500'
    },
    {
      title: 'Transport & Safety',
      features: ['Fleet Overview', 'Route Planning', 'Live Tracking', 'Driver Management'],
      icon: TruckIcon,
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'User Management',
      features: ['Multi-Role Support', 'Institution Admin', 'Bulk Onboarding', 'Permission Control'],
      icon: UserGroupIcon,
      color: 'from-purple-500 to-pink-500'
    }
  ]

  const stats = [
    { number: '2,500+', label: 'Students Tracked Daily', icon: UserGroupIcon },
    { number: '50+', label: 'Schools in East Africa', icon: BuildingOfficeIcon },
    { number: '99.9%', label: 'RFID Accuracy Rate', icon: QrCodeIcon },
    { number: '24/7', label: 'Real-time Monitoring', icon: ClockIcon },
  ]

  const testimonials = [
    {
      name: 'Dr. Grace Wanjiku',
      role: 'Principal, Nairobi Academy',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b647?w=150&h=150&fit=crop&crop=faces',
      content: 'SmartTag School has revolutionized how we manage student safety. The RFID tracking gives us real-time visibility, and parents love receiving instant notifications when their children arrive safely.',
      rating: 5,
      company: 'Nairobi Academy',
      location: 'Nairobi, Kenya'
    },
    {
      name: 'James Mwangi',
      role: 'IT Director, Greenwood Schools',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=faces',
      content: 'The multi-tenant platform is perfect for our network of 5 schools. The transport tracking has reduced parent complaints by 90% and improved our operational efficiency significantly.',
      rating: 5,
      company: 'Greenwood Schools Network',
      location: 'Kampala, Uganda'
    },
    {
      name: 'Dr. Amina Hassan',
      role: 'School Board Chairperson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=faces',
      content: 'The country code feature for international communication and the comprehensive analytics have made our international school operations seamless. Highly recommended for modern educational institutions.',
      rating: 5,
      company: 'International School of Dar es Salaam',
      location: 'Dar es Salaam, Tanzania'
    },
  ]

  const pricingPlans = [
    {
      name: 'Starter School',
      price: 'KSH 25,000',
      period: '/month',
      description: 'Perfect for small primary schools',
      studentLimit: 'Up to 300 students',
      features: [
        'RFID student tracking',
        'Basic attendance monitoring',
        'Parent SMS notifications',
        'Mobile app access',
        'Email support',
        'Basic reporting dashboard',
        'Single school tenant'
      ],
      highlighted: false,
      color: 'blue',
    },
    {
      name: 'Professional',
      price: 'KSH 65,000',
      period: '/month',
      description: 'For growing secondary schools',
      studentLimit: 'Up to 1,000 students',
      features: [
        'Everything in Starter',
        'Transport management & GPS',
        'RFID POS terminals',
        'Advanced analytics dashboard',
        'Multi-country phone support',
        'Priority 24/7 support',
        'API access & integrations',
        'Emergency alert system',
        'Staff training included'
      ],
      highlighted: true,
      color: 'purple',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For school networks & districts',
      studentLimit: 'Unlimited students',
      features: [
        'Everything in Professional',
        'Multi-tenant management',
        'Custom country code setup',
        'White-label branding',
        'Dedicated account manager',
        'On-site installation',
        'Custom feature development',
        'SLA guarantees',
        'Advanced security compliance'
      ],
      highlighted: false,
      color: 'emerald',
    },
  ]

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  const parallaxY = scrollY * 0.5

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Floating Navigation */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="fixed w-full z-50 top-4"
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="glass rounded-2xl border border-white/20 backdrop-blur-xl shadow-2xl">
            <div className="flex justify-between items-center py-4 px-6">
              {/* Logo */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <QrCodeIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    SmartTag
                  </span>
                  <span className="text-sm text-gray-500 block -mt-1">School</span>
                </div>
              </motion.div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-8">
                {['Features', 'Modules', 'Pricing', 'Demo', 'Contact'].map((item) => (
                  <motion.a
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors relative group"
                  >
                    {item}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 group-hover:w-full transition-all duration-300"></span>
                  </motion.a>
                ))}
                <Link to="/login" className="btn btn-ghost">Admin Login</Link>
                <motion.a 
                  href="#demo" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 hover:from-blue-700 hover:to-purple-700 shadow-lg"
                >
                  Get Started
                </motion.a>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <motion.div
                  animate={{ rotate: mobileMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {mobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </motion.div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mt-4 mx-4"
            >
              <div className="glass rounded-2xl border border-white/20 backdrop-blur-xl shadow-2xl p-6">
                <div className="space-y-4">
                  {['Features', 'Modules', 'Pricing', 'Demo', 'Contact'].map((item) => (
                    <a
                      key={item}
                      href={`#${item.toLowerCase()}`}
                      className="block py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item}
                    </a>
                  ))}
                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    <Link to="/login" className="block btn btn-ghost w-full">Admin Login</Link>
                    <a href="#demo" className="block btn bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 w-full">
                      Get Started
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.4) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.4) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(16, 185, 129, 0.3) 0%, transparent 50%)
              `,
              transform: `translateY(${parallaxY}px)`,
            }}
          />
          
          {/* Floating RFID Elements */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-lg opacity-10 flex items-center justify-center"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -40, 0],
                rotate: [0, 180, 360],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            >
              <QrCodeIcon className="w-4 h-4 text-blue-600" />
            </motion.div>
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <span className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 text-blue-700 text-sm font-medium mb-6">
                <SparklesIcon className="w-4 h-4 mr-2" />
                🇰🇪 Proudly serving East African schools - Now with global reach
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-5xl lg:text-7xl font-bold mb-8 leading-tight"
            >
              <span className="bg-gradient-to-r from-gray-900 via-blue-700 to-purple-700 bg-clip-text text-transparent">
                Smart Wristbands,
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Smarter Schools
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl lg:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed"
            >
              Revolutionary RFID-based school management platform with real-time student tracking, 
              comprehensive transport management, and global communication support for modern educational institutions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
            >
              <motion.a
                href="#demo"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 hover:from-blue-700 hover:to-purple-700 shadow-xl px-8 py-4 text-lg"
              >
                <QrCodeIcon className="w-5 h-5 mr-2" />
                Start Your Journey
              </motion.a>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-lg btn-ghost border-2 border-gray-300 hover:border-blue-300 hover:bg-blue-50 px-8 py-4 text-lg"
              >
                <PlayIcon className="w-5 h-5 mr-2" />
                Watch Demo
              </motion.button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-500"
            >
              <div className="flex items-center">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                30-day free trial
              </div>
              <div className="flex items-center">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                RFID hardware included
              </div>
              <div className="flex items-center">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                Global phone support
              </div>
              <div className="flex items-center">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                Multi-tenant ready
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-gray-400 rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-purple-50/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-10 w-72 h-72 bg-gradient-to-r from-blue-300 to-purple-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
          <div className="absolute top-1/3 right-10 w-72 h-72 bg-gradient-to-r from-purple-300 to-pink-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Revolutionary RFID
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                School Technology
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete ecosystem of SmartTag modules designed for modern educational institutions worldwide
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {smartTagFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: feature.delay }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group relative"
              >
                <div className="h-full p-8 bg-white/70 backdrop-blur-sm rounded-3xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-sm font-semibold text-blue-600 mb-2">{feature.module}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Modules Section */}
      <section id="modules" className="py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Complete Platform
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Modules
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Integrated modules that work together seamlessly to provide comprehensive school management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platformModules.map((module, index) => (
              <motion.div
                key={module.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className="h-full p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className={`w-14 h-14 bg-gradient-to-br ${module.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <module.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{module.title}</h3>
                  <ul className="space-y-2">
                    {module.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-gray-600">
                        <CheckIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-32 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                <span className="text-white">
                  See SmartTag School
                </span>
                <br />
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  in Action
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Experience our comprehensive school management platform with real-time RFID tracking, 
                multi-country communication, and advanced analytics designed for modern educational institutions.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'Live RFID student tracking dashboard',
                  'Multi-country phone number support',
                  'Real-time transport GPS monitoring',
                  'Advanced multi-tenant analytics',
                  'Instant emergency notifications',
                  'Comprehensive fleet management'
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center"
                  >
                    <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mr-3">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </motion.div>
                ))}
              </div>
              <div className="flex space-x-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn btn-lg bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 border-0 hover:from-yellow-500 hover:to-orange-600 shadow-xl font-semibold"
                >
                  <EyeIcon className="w-5 h-5 mr-2" />
                  Live Demo
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn btn-lg btn-ghost text-white border-white hover:bg-white/10"
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  Call +254 700 000 000
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-gray-900 font-semibold">SmartTag School Dashboard</h3>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                      <div className="text-blue-100 text-sm">Students Present</div>
                      <div className="text-2xl font-bold">2,487</div>
                      <div className="text-blue-200 text-xs flex items-center">
                        <QrCodeIcon className="w-3 h-3 mr-1" />
                        RFID Active
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                      <div className="text-green-100 text-sm">Buses Active</div>
                      <div className="text-2xl font-bold">12</div>
                      <div className="text-green-200 text-xs flex items-center">
                        <MapPinIcon className="w-3 h-3 mr-1" />
                        GPS Tracking
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-gray-700 text-sm mb-3 font-medium">Real-time Activity</div>
                    <div className="space-y-2">
                      {[
                        { text: 'Bus #7 (KE) arrived - 45 students', flag: '🇰🇪', time: '2m ago' },
                        { text: 'Emergency drill completed', flag: '🚨', time: '5m ago' },
                        { text: 'Parent pickup (UG) - Sarah M.', flag: '🇺🇬', time: '8m ago' }
                      ].map((activity, i) => (
                        <div key={i} className="flex items-center justify-between text-gray-600 text-sm">
                          <div className="flex items-center">
                            <span className="mr-2">{activity.flag}</span>
                            <span>{activity.text}</span>
                          </div>
                          <span className="text-xs text-gray-400">{activity.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl"
              >
                <QrCodeIcon className="w-8 h-8 text-white" />
              </motion.div>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
                className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-green-400 to-blue-500 rounded-xl flex items-center justify-center shadow-xl"
              >
                <SignalIcon className="w-6 h-6 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-32 bg-gradient-to-br from-blue-50 to-purple-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Trusted by Schools
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Across East Africa
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              Join educational institutions across Kenya, Uganda, and Tanzania that trust SmartTag School
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-12 shadow-xl border border-white/20">
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <StarIcon key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  <blockquote className="text-xl lg:text-2xl text-gray-700 mb-8 italic leading-relaxed">
                    "{testimonials[currentTestimonial].content}"
                  </blockquote>
                  
                  <div className="flex items-center justify-center space-x-4">
                    <img
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].name}
                      className="w-16 h-16 rounded-full shadow-lg"
                    />
                    <div className="text-left">
                      <div className="font-bold text-gray-900 text-lg">
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className="text-gray-600">{testimonials[currentTestimonial].role}</div>
                      <div className="text-blue-600 text-sm font-medium">
                        {testimonials[currentTestimonial].company}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {testimonials[currentTestimonial].location}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-center space-x-4 mt-8">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={prevTestimonial}
                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <ChevronLeftIcon className="w-6 h-6 text-gray-600" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={nextTestimonial}
                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <ChevronRightIcon className="w-6 h-6 text-gray-600" />
              </motion.button>
            </div>

            {/* Dots */}
            <div className="flex justify-center space-x-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentTestimonial 
                      ? 'bg-blue-600' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Transparent Pricing
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                for Every School
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Affordable plans designed for East African schools. All plans include RFID hardware, setup, and training.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className={`relative group ${plan.highlighted ? 'lg:-mt-8' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className={`h-full p-8 lg:p-10 rounded-3xl border-2 transition-all duration-300 ${
                  plan.highlighted 
                    ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 shadow-2xl' 
                    : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-xl'
                }`}>
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    <div className="mb-2">
                      <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-gray-600 text-lg">{plan.period}</span>
                    </div>
                    <p className="text-sm text-gray-500">{plan.studentLimit}</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <div className="w-5 h-5 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <CheckIcon className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-full py-4 px-6 rounded-2xl font-semibold transition-all duration-300 ${
                      plan.highlighted
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg'
                        : 'border-2 border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    {plan.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional pricing info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="text-gray-600 mb-6">
              All plans include RFID wristbands, hardware setup, staff training, and 24/7 support in Swahili and English.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center">
                <CheckIcon className="w-4 h-4 text-green-500 mr-2" />
                No hidden fees
              </div>
              <div className="flex items-center">
                <CheckIcon className="w-4 h-4 text-green-500 mr-2" />
                Local currency support
              </div>
              <div className="flex items-center">
                <CheckIcon className="w-4 h-4 text-green-500 mr-2" />
                Mobile money payments
              </div>
              <div className="flex items-center">
                <CheckIcon className="w-4 h-4 text-green-500 mr-2" />
                Cancel anytime
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
              Ready to Revolutionize
              <br />
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Your School?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Join schools across East Africa that trust SmartTag School to enhance safety, 
              streamline operations, and connect with parents globally.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.a
                href="#demo"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-lg bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 border-0 hover:from-yellow-500 hover:to-orange-600 shadow-xl font-semibold px-8"
              >
                <QrCodeIcon className="w-5 h-5 mr-2" />
                Start Your Journey
              </motion.a>
              <Link
                to="/login"
                className="btn btn-lg btn-ghost text-white border-white hover:bg-white/10 px-8"
              >
                School Admin Login
              </Link>
            </div>
            
            <div className="mt-12 pt-8 border-t border-white/20">
              <p className="text-gray-400 mb-4">Connect with us directly:</p>
              <div className="flex justify-center space-x-8 text-gray-300">
                <div className="flex items-center">
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  <span>+254 700 000 000 (Kenya)</span>
                </div>
                <div className="flex items-center">
                  <EnvelopeIcon className="w-5 h-5 mr-2" />
                  <span>hello@smarttagschool.com</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <QrCodeIcon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    SmartTag
                  </span>
                  <span className="text-lg text-gray-400 block -mt-1">School</span>
                </div>
              </div>
              <p className="text-gray-400 mb-8 max-w-md leading-relaxed">
                Transforming education across East Africa with cutting-edge RFID technology, 
                real-time tracking, and comprehensive school management solutions.
              </p>
              <div className="text-sm text-gray-500 mb-6">
                <p className="font-semibold text-gray-400 mb-2">Proudly serving:</p>
                <div className="flex space-x-4">
                  <span>🇰🇪 Kenya</span>
                  <span>🇺🇬 Uganda</span>
                  <span>🇹🇿 Tanzania</span>
                </div>
              </div>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-6">Platform</h3>
              <ul className="space-y-4">
                {['RFID Tracking', 'Transport Management', 'Analytics', 'Multi-Tenant', 'Global Support', 'API Access'].map((item) => (
                  <li key={item}>
                    <a href="#" className="hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-6">Support</h3>
              <ul className="space-y-4">
                {['Help Center', 'Training', 'Contact Sales', 'System Status', 'Hardware Setup', 'Documentation'].map((item) => (
                  <li key={item}>
                    <a href="#" className="hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col lg:flex-row justify-between items-center">
              <div className="mb-6 lg:mb-0">
                <p className="text-gray-400 mb-2">
                  © 2024 SmartTag School. All rights reserved.
                </p>
                <p className="text-sm text-gray-500">
                  Smart Wristbands, Smarter Schools™ - Empowering Education in East Africa
                </p>
              </div>
              <div className="flex flex-col lg:flex-row lg:space-x-8 space-y-2 lg:space-y-0 text-sm">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Data Protection</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage